import {
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  TrendingDown,
  TrendingUp,
  Banknote,
  Download,
} from 'lucide-react'

import { formatDate, formatTableMoney } from '../utils/formatters'
import ExpensesPeriodFilter from '../components/expenses/ExpensesPeriodFilter'

const CATEGORIES = [
  'Aluguel',
  'Material',
  'Dentistas',
  'Mercado',
  'Café',
  'Luz',
  'Agua',
  'Internet',
  'Outros',
]

// Cores usadas nos badges/legenda do donut, na ordem do design
const CATEGORY_COLORS = {
  'Aluguel': '#9CA3AF',      // cinza
  'Material': '#B8860B',     // dourado (cor principal)
  'Dentistas': '#5A5147',    // marrom escuro
  'Mercado': '#F97316',
  'Café': '#D97706',
  'Luz': '#EAB308',
  'Agua': '#06B6D4',
  'Internet': '#6366F1',
  'Outros': '#6B7280',
}

export default function ExpensesPage({
  expenses = [],
  allExpenses = [],
  isExporting = false,
  isLoading = false,
  onDeleteExpense,
  onEditExpense,
  onExportExpenses,
  onFilterExpenses,
  onNewExpense,
  onPageChange,
  onToggleActionMenu,
  openActionExpenseId,
  pagination,
  expenseSearch,
  setExpenseSearch,
  categoryFilter,
  setCategoryFilter,
  expensesPeriod,
  onExpensesPeriodChange,
}) {
  // Usar os dados paginados da API diretamente para a tabela
  const filteredExpenses = expenses

  // Filtrar despesas baseado no período selecionado
  const now = new Date()
  const currentMonth = now.getMonth()
  const currentYear = now.getFullYear()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)

  const getFilteredExpensesByPeriod = () => {
    switch (expensesPeriod) {
      case 'month':
        return allExpenses.filter((expense) => {
          const expenseDate = new Date(expense.created_at)
          return expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear
        })
      case 'week':
        return allExpenses.filter((expense) => {
          const expenseDate = new Date(expense.created_at)
          return expenseDate >= weekAgo
        })
      case 'day':
        return allExpenses.filter((expense) => {
          const expenseDate = new Date(expense.created_at)
          const expenseDay = new Date(expenseDate.getFullYear(), expenseDate.getMonth(), expenseDate.getDate())
          return expenseDay.getTime() === today.getTime()
        })
      case 'all':
      default:
        return allExpenses
    }
  }

  const periodExpenses = getFilteredExpensesByPeriod()
  const periodTotal = periodExpenses.reduce((sum, e) => sum + e.value, 0)

  // Calcular variação em relação ao período anterior (apenas para mês)
  let changePct = 0
  if (expensesPeriod === 'month') {
    const lastMonthDate = new Date(currentYear, currentMonth - 1, 1)
    const lastMonthTotal = allExpenses
      .filter((expense) => {
        const d = new Date(expense.created_at)
        return d.getMonth() === lastMonthDate.getMonth() && d.getFullYear() === lastMonthDate.getFullYear()
      })
      .reduce((sum, e) => sum + e.value, 0)

    changePct = lastMonthTotal > 0
      ? ((periodTotal - lastMonthTotal) / lastMonthTotal) * 100
      : 0
  }

  // Totais por categoria, para "Maior Custo" e "Distribuição"
  const categoryTotals = CATEGORIES
    .map((category) => ({
      category,
      total: periodExpenses.filter((e) => e.category === category).reduce((sum, e) => sum + e.value, 0),
      count: periodExpenses.filter((e) => e.category === category).length,
    }))
    .filter((c) => c.total > 0)
    .sort((a, b) => b.total - a.total)

  const topCategory = categoryTotals[0]
  const topThreeCategories = categoryTotals.slice(0, 3).map((c) => ({
    ...c,
    pct: periodTotal > 0 ? Math.round((c.total / periodTotal) * 100) : 0,
  }))

  return (
    <div>
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-normal text-[#1A1A1A] lg:text-3xl">
            Saídas
          </h1>
          <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-[#5A5147] sm:max-w-2xl lg:text-base">
            Gerencie despesas e saídas da clínica, categorize gastos e mantenha o controle financeiro.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <ExpensesPeriodFilter value={expensesPeriod} onChange={onExpensesPeriodChange} />
          <button
            className="flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-4 text-sm font-bold text-white shadow-[0_12px_24px_rgba(26,26,26,0.12)] transition hover:bg-[#2C2A28] sm:w-auto whitespace-nowrap"
            type="button"
            onClick={onNewExpense}
          >
            <Plus size={18} strokeWidth={2.8} aria-hidden="true" />
            Nova Saída
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <section className="mb-5 grid gap-4 lg:grid-cols-3">
        <TotalMonthCard total={periodTotal} changePct={changePct} period={expensesPeriod} />
        <TopCostCard category={topCategory} />
        <DistributionCard categories={topThreeCategories} />
      </section>

      <section className="mb-5 rounded-xl bg-white p-4 shadow-[0_18px_45px_rgba(26,26,26,0.05)]">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row">
            <div className="flex h-14 min-w-0 flex-1 items-center gap-3 rounded-lg border border-[#EEE8DF] bg-[#FBFAF8] px-4 text-[#5A5147] md:h-10 md:gap-3 md:rounded-md">
              <Search className="h-6 w-6 shrink-0 md:h-[18px] md:w-[18px]" strokeWidth={2.8} aria-hidden="true" />
              <input
                className="h-full min-w-0 flex-1 bg-transparent text-base font-semibold outline-none placeholder:text-[#5A5147] md:text-sm"
                placeholder="Buscar saída..."
                value={expenseSearch}
                onChange={(event) => setExpenseSearch(event.target.value)}
              />
            </div>

            <div className="flex h-10 items-center rounded-full border border-[#D6CDBF] bg-white px-4 text-sm font-bold text-[#5A5147]">
              <select
                className="bg-transparent outline-none w-full"
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <option value="">Todas categorias</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid min-w-0 grid-cols-1 gap-2 md:flex md:justify-end">
            <button
              className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-full border border-[#D6CDBF] bg-white px-3 text-xs font-bold text-[#8A6819] shadow-sm transition hover:bg-[#FBF8F4] disabled:cursor-not-allowed disabled:opacity-60 md:gap-2 md:px-4 md:text-sm"
              disabled={isExporting}
              type="button"
              onClick={onExportExpenses}
            >
              <Download size={14} strokeWidth={2.8} aria-hidden="true" />
              {isExporting ? 'Exportando...' : 'Exportar'}
            </button>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl bg-white shadow-[0_18px_45px_rgba(26,26,26,0.05)]">
        <div className="divide-y divide-[#E5DED3] md:hidden">
          {filteredExpenses.length ? (
            filteredExpenses.map((expense, index) => (
              <ExpenseMobileCard
                key={expense.id}
                index={index}
                isActionOpen={openActionExpenseId === expense.id}
                expense={expense}
                onDelete={onDeleteExpense}
                onEdit={onEditExpense}
                onToggleActions={onToggleActionMenu}
              />
            ))
          ) : (
            <div className="px-4 py-8 text-center text-sm font-semibold text-[#5A5147]">
              {isLoading ? 'Carregando saídas...' : 'Nenhuma saída cadastrada'}
            </div>
          )}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[900px] border-collapse text-left">
            <thead className="bg-[#FBFAF8] text-[#5A5147]">
              <tr>
                <TableHead>Descrição</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Data</TableHead>
                <TableHead align="right">Valor<br />(R$)</TableHead>
                <TableHead align="center">Ações</TableHead>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length ? (
                filteredExpenses.map((expense, index) => (
                  <tr
                    className={`border-b border-[#E5DED3] ${
                      index % 2 === 1 ? 'bg-[#FCFBF9]' : 'bg-white'
                    }`}
                    key={expense.id}
                  >
                    <td className="max-w-[280px] px-4 py-3 text-sm font-bold text-[#1A1A1A]">
                      {expense.description}
                    </td>
                    <td className="px-4 py-3">
                      <CategoryBadge value={expense.category} />
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#5A5147]">
                      {formatDate(expense.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-[#1A1A1A]">
                      {formatTableMoney(expense.value)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <ExpenseActions
                        isOpen={openActionExpenseId === expense.id}
                        expense={expense}
                        onDelete={onDeleteExpense}
                        onEdit={onEditExpense}
                        onToggle={onToggleActionMenu}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-8 text-center text-sm font-semibold text-[#5A5147]" colSpan={5}>
                    {isLoading ? 'Carregando saídas...' : 'Nenhuma saída cadastrada'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-[#E5DED3] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-[#5A5147]">
            Mostrando {filteredExpenses.length ? 1 : 0} a {filteredExpenses.length} de{' '}
            {pagination.total} saídas
          </p>
          <div className="flex items-center gap-2 text-sm font-bold text-[#5A5147] sm:gap-3">
            <button
              className="disabled:opacity-35"
              disabled={pagination.page <= 1 || isLoading}
              type="button"
              aria-label="Página anterior"
              onClick={() => onPageChange(pagination.page - 1)}
            >
              <ChevronLeft size={18} />
            </button>
            {getPaginationItems(pagination.page, Math.max(pagination.total_pages, 1)).map(
              (item, index) =>
                item === 'ellipsis' ? (
                  <span key={`ellipsis-${index}`} className="hidden px-0.5 sm:inline">
                    ...
                  </span>
                ) : (
                  <button
                    key={item}
                    className={`flex h-9 min-w-9 items-center justify-center rounded-full px-2 transition disabled:opacity-35 ${
                      item === pagination.page
                        ? 'bg-[#1A1A1A] text-white'
                        : 'text-[#5A5147] hover:bg-[#FBF8F4]'
                    }`}
                    disabled={isLoading || item === pagination.page}
                    type="button"
                    aria-current={item === pagination.page ? 'page' : undefined}
                    aria-label={`Página ${item}`}
                    onClick={() => onPageChange(item)}
                  >
                    {item}
                  </button>
                ),
            )}
            <button
              className="disabled:opacity-35"
              disabled={pagination.page >= pagination.total_pages || isLoading}
              type="button"
              aria-label="Próxima página"
              onClick={() => onPageChange(pagination.page + 1)}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

function TableHead({ children, align = 'left' }) {
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''

  return (
    <th className={`px-5 py-3 text-xs font-bold uppercase leading-4 ${alignClass}`}>
      {children}
    </th>
  )
}

function CategoryBadge({ value }) {
  const colors = {
    'Aluguel': 'bg-gray-100 text-gray-700',
    'Material': 'bg-amber-100 text-amber-800',
    'Dentistas': 'bg-orange-100 text-orange-900',
    'Mercado': 'bg-orange-100 text-orange-800',
    'Café': 'bg-amber-100 text-amber-800',
    'Luz': 'bg-yellow-100 text-yellow-800',
    'Agua': 'bg-cyan-100 text-cyan-800',
    'Internet': 'bg-indigo-100 text-indigo-800',
    'Outros': 'bg-gray-100 text-gray-800',
  }
  const colorClass = colors[value] || colors['Outros']
  const dotColor = CATEGORY_COLORS[value] || CATEGORY_COLORS['Outros']

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${colorClass}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: dotColor }} />
      {value}
    </span>
  )
}

function TotalMonthCard({ total, changePct, period }) {
  const getPeriodLabel = () => {
    switch (period) {
      case 'month':
        return 'Total em Saídas (Mês)'
      case 'week':
        return 'Total em Saídas (Semana)'
      case 'day':
        return 'Total em Saídas (Hoje)'
      case 'all':
        return 'Total em Saídas (Geral)'
      default:
        return 'Total em Saídas (Mês)'
    }
  }

  const getComparisonLabel = () => {
    if (period === 'month') {
      return 'em relação ao mês anterior'
    }
    return null
  }

  const comparisonLabel = getComparisonLabel()

  return (
    <div className="rounded-xl bg-gradient-to-br from-[#E4C77D] to-[#B8860B] p-4 shadow-[0_18px_45px_rgba(184,134,11,0.25)]">
      <p className="text-xs font-bold uppercase text-[#4A3B10]">{getPeriodLabel()}</p>
      <p className="mt-3 text-2xl font-bold tracking-normal text-[#2C2200]">
        {formatTableMoney(total)}
      </p>
      {comparisonLabel && (
        <div className="mt-4 inline-flex items-center gap-1 rounded-full bg-white/70 px-3 py-1 text-xs font-bold text-[#7A1F1F]">
          {changePct >= 0 ? (
            <TrendingUp size={14} strokeWidth={2.6} aria-hidden="true" />
          ) : (
            <TrendingDown size={14} strokeWidth={2.6} aria-hidden="true" />
          )}
          {changePct >= 0 ? '+' : ''}{changePct.toFixed(1)}% {comparisonLabel}
        </div>
      )}
    </div>
  )
}

function TopCostCard({ category }) {
  return (
    <div className="rounded-xl bg-white p-4 shadow-[0_18px_45px_rgba(26,26,26,0.05)]">
      <p className="text-xs font-bold uppercase text-[#5A5147]">Maior Custo</p>
      <p className="mt-2 text-xl font-bold tracking-normal text-[#B8860B]">
        {category ? category.category : '—'}
      </p>
      <div className="mt-4 rounded-lg bg-[#FBFAF8] px-4 py-3">
        <p className="text-xs font-bold uppercase text-[#5A5147]">Total Acumulado</p>
        <p className="mt-1 text-xl font-bold tracking-normal text-[#1A1A1A]">
          {formatTableMoney(category ? category.total : 0)}
        </p>
      </div>
    </div>
  )
}

function DistributionCard({ categories }) {
  const total = categories.reduce((sum, c) => sum + c.pct, 0)
  let cumulative = 0
  const gradientStops = categories.map((c) => {
    const start = cumulative
    cumulative += c.pct
    return `${CATEGORY_COLORS[c.category] || '#9CA3AF'} ${start}% ${cumulative}%`
  })
  const remaining = 100 - cumulative
  if (remaining > 0) gradientStops.push(`#E5DED3 ${cumulative}% 100%`)

  return (
    <div className="rounded-xl bg-white p-4 shadow-[0_18px_45px_rgba(26,26,26,0.05)]">
      <p className="mb-4 text-xs font-bold uppercase text-[#5A5147]">Distribuição</p>
      <div className="flex items-center gap-5">
        <div
          className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-full"
          style={{ background: `conic-gradient(${gradientStops.join(', ')})` }}
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#B8860B]">
            <Banknote size={22} strokeWidth={2.4} aria-hidden="true" />
          </div>
        </div>
        <ul className="flex flex-col gap-2">
          {categories.map((c) => (
            <li key={c.category} className="flex items-center gap-2 text-sm font-semibold text-[#5A5147]">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: CATEGORY_COLORS[c.category] || '#9CA3AF' }}
              />
              {c.category} {c.pct}%
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function ExpenseActions({ expense, isOpen, onEdit, onDelete, onToggle }) {
  return (
    <div className="inline-flex justify-center">
      <button
        className={`flex h-8 w-8 items-center justify-center rounded-md text-[#5A5147] transition hover:bg-[#FBF8F4] ${
          isOpen ? 'bg-[#F8E1E7] text-[#5A5147]' : ''
        }`}
        type="button"
        aria-label="Ações da saída"
        onClick={(event) => onToggle(expense, event)}
      >
        <MoreVertical size={18} />
      </button>
    </div>
  )
}

export function ActionMenuOverlay({ menu, onClose, onDelete, onEdit }) {
  return (
    <>
      <button
        className="fixed inset-0 z-30 cursor-default bg-transparent"
        type="button"
        aria-label="Fechar menu de ações"
        onClick={onClose}
      />
      <div
        className="fixed z-40 w-36 overflow-hidden rounded-lg border border-[#E5DED3] bg-white py-1 text-left shadow-[0_12px_30px_rgba(17,24,39,0.16)]"
        style={{ left: `${menu.left}px`, top: `${menu.top}px` }}
      >
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-sm font-semibold text-[#1A1A1A] transition hover:bg-[#F8E1E7]"
          type="button"
          onClick={() => onEdit(menu.expense)}
        >
          <Pencil size={15} />
          Editar
        </button>
        <button
          className="flex w-full items-center gap-2 px-3 py-2 text-sm font-semibold text-[#C91D1D] transition hover:bg-[#FFF1F1]"
          type="button"
          onClick={() => onDelete(menu.expense)}
        >
          <Trash2 size={15} />
          Excluir
        </button>
      </div>
    </>
  )
}

function ExpenseMobileCard({ expense, index, isActionOpen, onDelete, onEdit, onToggleActions }) {
  const categoryColor = CATEGORY_COLORS[expense.category] || CATEGORY_COLORS['Outros']
  
  // Abreviações para categorias com mesma inicial
  const getAbbreviation = (category) => {
    const abbreviations = {
      'Aluguel': 'AL',
      'Agua': 'AG',
      'Material': 'MT',
      'Dentistas': 'DE',
      'Mercado': 'ME',
      'Café': 'CF',
      'Luz': 'LZ',
      'Internet': 'IN',
      'Outros': 'OT',
    }
    return abbreviations[category] || category.substring(0, 2).toUpperCase()
  }
  
  return (
    <article className="grid gap-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div 
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: categoryColor }}
          >
            <span className="text-xs font-bold text-white">{getAbbreviation(expense.category)}</span>
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold text-[#1A1A1A]">{expense.description}</h3>
            <p className="mt-0.5 truncate text-sm font-semibold text-[#5A5147]">
              {expense.category}
            </p>
          </div>
        </div>
        <ExpenseActions
          isOpen={isActionOpen}
          expense={expense}
          onDelete={onDelete}
          onEdit={onEdit}
          onToggle={onToggleActions}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold text-[#8A6819]">Valor</p>
          <p className="mt-1 text-sm font-bold text-[#1A1A1A]">{formatTableMoney(expense.value)}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-[#8A6819]">Data</p>
          <p className="mt-1 text-sm font-bold text-[#1A1A1A]">{formatDate(expense.created_at)}</p>
        </div>
      </div>
    </article>
  )
}

function getPaginationItems(current, total) {
  if (total <= 1) return [1]
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1)
  }

  const items = []
  const pushPage = (page) => {
    if (items[items.length - 1] !== page) items.push(page)
  }

  pushPage(1)
  if (current > 3) items.push('ellipsis')

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)
  for (let page = start; page <= end; page += 1) {
    pushPage(page)
  }

  if (current < total - 2) items.push('ellipsis')
  pushPage(total)

  return items
}
